import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addMonths, format, startOfDay, subMonths } from 'date-fns';
import { Container, Button, Form, Col, Row, InputGroup, Alert, SegmentedControl } from '@/components/ui';
import { usePermissions } from '../../hooks/usePermissions';
import { useDeviceSize } from '../../hooks/useDeviceSize';
import FiltersCard from '../../components/FiltersCard';
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown';
import { taskService } from '../../services/quantifiedTaskService';
import activityService from '../../services/activityService';
import ResourceGanttChart from './components/ResourceGanttChart';
import TaskGanttChart from './components/TaskGanttChart';
import ScheduleEquipmentTable from './components/ScheduleEquipmentTable';
import ScheduleTaskTable from './components/ScheduleTaskTable';
import ScheduleChartLegend from './components/ScheduleChartLegend';
import ScheduleTimelineScaleSwitcher, {
  type ScheduleTimelineScale,
} from './components/ScheduleTimelineScaleSwitcher';
import PrintScheduleModal from './components/PrintScheduleModal';
import { getScheduleVehicles } from '../../services/scheduleService';
import type { ScheduleResource } from '../../services/scheduleService';
import { ActivityType, type Activity } from '../../types/Activity';
import {
  toScheduleListParam,
  type ScheduleResourcePageFilters,
} from './utils/scheduleResourceAggregation';
import {
  formatScheduleTaskStatusLabel,
  SCHEDULE_PAGE_DEFAULT_STATUS_FILTER,
  SCHEDULE_PAGE_STATUS_FILTER_OPTIONS,
} from './utils/scheduleTaskStatuses';

/** Work Type options for the multiselect. No selection = every work type (server default). */
const SCHEDULE_WORK_TYPE_OPTIONS: string[] = ['Quantified', 'Routine'];

type ViewMode = 'chart' | 'table';
type ResourceOrTask = 'vehicle' | 'task' | 'people';

/** Filter fields edited in the card; task text search is applied separately (Search button). */
type ScheduleFilterFields = Omit<ScheduleResourcePageFilters, 'taskSearch'>;

/** Default window: 2 months in the past through 4 months in the future (relative to today). */
function createDefaultScheduleFilters(): ScheduleFilterFields {
  const today = startOfDay(new Date());
  const start = subMonths(today, 2);
  const end = addMonths(today, 4);
  return {
    serviceAreas: [],
    foremanAreas: [],
    equipmentTypes: [],
    projectIds: [],
    activityIds: [],
    activityNumbers: [],
    statuses: [...SCHEDULE_PAGE_DEFAULT_STATUS_FILTER],
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
    workTypes: ['Quantified'],
  };
}

function createDefaultScheduleFiltersForResource(resourceOrTask: ResourceOrTask): ScheduleFilterFields {
  const base = createDefaultScheduleFilters();
  if (resourceOrTask === 'vehicle') {
    // Equipment view hides Foreman Area and Work Type; an empty work-type selection means "all",
    // which the API resolves the same way the old explicit "All" value did.
    return { ...base, foremanAreas: [], workTypes: [] };
  }
  return { ...base, equipmentTypes: [] };
}

/** Multiset equality without sorting — avoids per-comparison allocations. */
function sameNumberMultiset(a: number[], b: number[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const counts = new Map<number, number>();
  for (let i = 0; i < a.length; i += 1) {
    const n = a[i];
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  for (let i = 0; i < b.length; i += 1) {
    const n = b[i];
    const c = counts.get(n);
    if (c === undefined || c === 0) return false;
    counts.set(n, c - 1);
  }
  return true;
}

function sameTrimmedStringMultiset(a: string[] | undefined, b: string[] | undefined): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa === bb) return true;
  if (aa.length !== bb.length) return false;
  const counts = new Map<string, number>();
  for (let i = 0; i < aa.length; i += 1) {
    const k = String(aa[i]).trim();
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  for (let i = 0; i < bb.length; i += 1) {
    const k = String(bb[i]).trim();
    const c = counts.get(k);
    if (c === undefined || c === 0) return false;
    counts.set(k, c - 1);
  }
  return true;
}

/**
 * Value equality for the pending/applied filter pair.
 *
 * Every dropdown field is an array now, so each one MUST go through a multiset comparison. A `===`
 * here would compare references: `updatePending` always produces a fresh array, so Apply would look
 * permanently dirty (and "Clear" would never settle back to the baseline).
 *
 * Only `startDate` / `endDate` remain scalar string compares.
 */
function scheduleFilterFieldsEqual(a: ScheduleFilterFields, b: ScheduleFilterFields): boolean {
  return (
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    sameTrimmedStringMultiset(a.serviceAreas, b.serviceAreas) &&
    sameTrimmedStringMultiset(a.foremanAreas, b.foremanAreas) &&
    sameTrimmedStringMultiset(a.equipmentTypes, b.equipmentTypes) &&
    sameTrimmedStringMultiset(a.workTypes, b.workTypes) &&
    sameNumberMultiset(a.projectIds, b.projectIds) &&
    sameNumberMultiset(a.activityIds, b.activityIds) &&
    sameTrimmedStringMultiset(a.activityNumbers, b.activityNumbers) &&
    sameTrimmedStringMultiset(a.statuses, b.statuses)
  );
}

interface FilterOptions {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{
    id: number;
    name: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  projects: Array<{
    id: number;
    name: string;
    projectNumber: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  contractYears?: string[];
}

export interface FillerActivity {
  activityId: number;
  activityName: string;
  activityNumber: string;
}

const SchedulePage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canPeopleSchedule = hasPermission('schedule:people');

  // A Gantt timeline needs horizontal room it simply doesn't have on a phone (the left resource
  // column alone eats most of 375px), so phones are routed to the tabular view regardless of the
  // toggle. The user's chart preference is preserved and restored when they're back on a tablet
  // or desktop — we override what's *rendered*, not what's selected.
  const { isMobile } = useDeviceSize();
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  const effectiveViewMode: ViewMode = isMobile ? 'table' : viewMode;
  const [resourceOrTask, setResourceOrTask] = useState<ResourceOrTask>('task');
  const [filterBaseline, setFilterBaseline] = useState<ScheduleFilterFields>(() =>
    createDefaultScheduleFiltersForResource('task')
  );
  const [pendingFilters, setPendingFilters] = useState<ScheduleFilterFields>(() =>
    createDefaultScheduleFiltersForResource('task')
  );
  const [appliedFilters, setAppliedFilters] = useState<ScheduleFilterFields>(() =>
    createDefaultScheduleFiltersForResource('task')
  );
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    serviceAreas: [],
    foremanAreas: [],
    projects: [],
    contractYears: [],
  });
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState<string[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleResource[] | null>(null);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [fillerActivities, setFillerActivities] = useState<FillerActivity[]>([]);
  const [activityOptions, setActivityOptions] = useState<Activity[]>([]);
  const [loadingActivityOptions, setLoadingActivityOptions] = useState(false);
  const [defaultContractYear, setDefaultContractYear] = useState<string>('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [chartTimelineScale, setChartTimelineScale] = useState<ScheduleTimelineScale>('weeks');

  const appliedWithSearch: ScheduleResourcePageFilters = useMemo(
    () => ({
      ...appliedFilters,
      taskSearch: searchTerm.trim(),
    }),
    [appliedFilters, searchTerm]
  );

  const hasUnappliedChanges = useMemo(
    () => !scheduleFilterFieldsEqual(pendingFilters, appliedFilters),
    [pendingFilters, appliedFilters]
  );

  const hasActiveFilters = useMemo(() => {
    if (searchTerm.trim().length > 0) return true;
    return !scheduleFilterFieldsEqual(appliedFilters, filterBaseline);
  }, [appliedFilters, filterBaseline, searchTerm]);

  // Keyed off the *effective* view mode so phones don't fetch resource-Gantt data that will
  // never be rendered.
  const showResourceGanttChart =
    effectiveViewMode === 'chart' && (resourceOrTask === 'vehicle' || resourceOrTask === 'people');

  useEffect(() => {
    if (!showResourceGanttChart) {
      setScheduleData(null);
      setLoadingSchedule(false);
    }
  }, [showResourceGanttChart]);

  useEffect(() => {
    if (!canPeopleSchedule && resourceOrTask === 'people') {
      setResourceOrTask('vehicle');
    }
  }, [canPeopleSchedule, resourceOrTask]);

  // Keep hidden filters from affecting results.
  // - Equipment view: hide Foreman Area + Work Type => clear them
  // - Task / People schedule: hide Equipment Type => clear it
  useEffect(() => {
    // Returning `prev` unchanged when there is nothing to clear keeps the three filter states
    // referentially stable — replacing an already-empty array would churn every downstream memo.
    const clearForVehicleView = (prev: ScheduleFilterFields): ScheduleFilterFields =>
      prev.foremanAreas.length === 0 && prev.workTypes.length === 0
        ? prev
        : { ...prev, foremanAreas: [], workTypes: [] };
    const clearForTaskView = (prev: ScheduleFilterFields): ScheduleFilterFields =>
      prev.equipmentTypes.length === 0 ? prev : { ...prev, equipmentTypes: [] };

    const clear = resourceOrTask === 'vehicle' ? clearForVehicleView : clearForTaskView;
    setPendingFilters(clear);
    setAppliedFilters(clear);
    setFilterBaseline(clear);
  }, [resourceOrTask]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ ...pendingFilters });
  }, [pendingFilters]);

  const handleClearFilters = useCallback(() => {
    const fresh = createDefaultScheduleFiltersForResource(resourceOrTask);
    setFilterBaseline(fresh);
    setPendingFilters({ ...fresh });
    setAppliedFilters({ ...fresh });
    setSearchInput('');
    setSearchTerm('');
  }, [resourceOrTask]);

  const toggleSearch = useCallback(() => {
    setIsSearchVisible((v) => {
      if (v) {
        setSearchTerm('');
        setSearchInput('');
      }
      return !v;
    });
  }, []);

  const handleSearch = useCallback(() => {
    setSearchTerm(searchInput.trim());
  }, [searchInput]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadFilterOptions = async () => {
      try {
        setLoadingFilters(true);
        const options = await taskService.getFilterOptions();
        if (cancelled) return;
        setFilterOptions({
          serviceAreas: options.serviceAreas || [],
          foremanAreas: options.foremanAreas || [],
          projects: options.projects || [],
          contractYears: options.contractYears || [],
        });
        const years = (options.contractYears || [])
          .map((y: string) => Number(y))
          .filter((n: number) => !Number.isNaN(n));
        if (years.length > 0) {
          setDefaultContractYear(String(Math.max(...years)));
        } else if ((options.contractYears || []).length > 0) {
          setDefaultContractYear(options.contractYears[0]);
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      } finally {
        if (!cancelled) setLoadingFilters(false);
      }
    };
    void loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadFillerActivities = async () => {
      try {
        const allActivities = await activityService.getAllActivities();
        const debris = (Array.isArray(allActivities) ? allActivities : []).filter((a: Activity) => {
          const name = String(a.activityName || '').trim().toLowerCase();
          return name === 'debris removal' || name.startsWith('debris removal');
        });

        if (debris.length === 0) {
          if (!cancelled) setFillerActivities([]);
          return;
        }

        const preferred =
          debris.find((a) => String(a.activityNumber || '').trim() === '108200') ?? debris[0];

        if (!cancelled) {
          setFillerActivities([
            {
              activityId: preferred.id,
              activityName: preferred.activityName,
              activityNumber: String(preferred.activityNumber || ''),
            },
          ]);
        }
      } catch (err) {
        console.error('Failed to load filler activities:', err);
      }
    };

    loadFillerActivities();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Stable key so the activity fetch re-runs on *content* change, not on a new array identity. */
  const pendingServiceAreasKey = pendingFilters.serviceAreas.join(',');

  useEffect(() => {
    let cancelled = false;
    const loadActivitiesForSelection = async () => {
      try {
        setLoadingActivityOptions(true);
        // Multiselect: one request per selected service area, merged. `/activities` only accepts a
        // single serviceAreaId, and asking for everything then filtering client-side would need
        // service-area data the activity payload does not always carry.
        const selectedIds = pendingServiceAreasKey
          .split(',')
          .map((s) => s.trim())
          .filter((s) => /^\d+$/.test(s))
          .map((s) => Number(s))
          .filter((n) => Number.isFinite(n) && n > 0);
        const rows =
          selectedIds.length > 0
            ? (
                await Promise.all(
                  selectedIds.map((serviceAreaId) => activityService.getAllActivities({ serviceAreaId }))
                )
              ).flatMap((r) => (Array.isArray(r) ? r : []))
            : await activityService.getAllActivities();
        const list = Array.isArray(rows) ? rows : [];
        const withoutOverhead = list.filter(
          (a) => String(a.activityType || '').toLowerCase() !== ActivityType.Overhead.toLowerCase()
        );
        const quantifiedClassificationOnly = withoutOverhead.filter(
          (a) => String(a.classification || '').trim().toLowerCase() === 'quantified'
        );

        // Match Task Management behavior: dedupe by activity number + name.
        const dedupedByNumberAndName = Array.from(
          quantifiedClassificationOnly.reduce((map, activity) => {
            const key = `${String(activity.activityNumber || '').trim()}|${String(activity.activityName || '').trim()}`;
            if (!map.has(key)) map.set(key, activity);
            return map;
          }, new Map<string, Activity>()).values()
        ).sort((a, b) => {
          const numberA = Number.parseInt(String(a.activityNumber || '').trim(), 10);
          const numberB = Number.parseInt(String(b.activityNumber || '').trim(), 10);
          const safeNumberA = Number.isNaN(numberA) ? 0 : numberA;
          const safeNumberB = Number.isNaN(numberB) ? 0 : numberB;
          if (safeNumberA !== safeNumberB) return safeNumberA - safeNumberB;
          return String(a.activityName || '').localeCompare(String(b.activityName || ''));
        });

        if (!cancelled) setActivityOptions(dedupedByNumberAndName);
      } catch (err) {
        console.error('Failed to load activities:', err);
        if (!cancelled) setActivityOptions([]);
      } finally {
        if (!cancelled) setLoadingActivityOptions(false);
      }
    };
    void loadActivitiesForSelection();
    return () => {
      cancelled = true;
    };
  }, [pendingServiceAreasKey]);

  const serviceAreasList = useMemo(
    () => (Array.isArray(filterOptions.serviceAreas) ? filterOptions.serviceAreas : []),
    [filterOptions.serviceAreas]
  );
  const foremanAreasList = useMemo(
    () => (Array.isArray(filterOptions.foremanAreas) ? filterOptions.foremanAreas : []),
    [filterOptions.foremanAreas]
  );
  const projectsList = useMemo(
    () => (Array.isArray(filterOptions.projects) ? filterOptions.projects : []),
    [filterOptions.projects]
  );
  /** Selected service areas as a lookup — options match on id *or* name, as before. */
  const pendingServiceAreaSet = useMemo(
    () => new Set(pendingServiceAreasKey.split(',').filter((s) => s.length > 0)),
    [pendingServiceAreasKey]
  );
  const foremanAreasForSelection = useMemo(() => {
    if (pendingServiceAreaSet.size === 0) return foremanAreasList;
    return foremanAreasList.filter(
      (fa) => pendingServiceAreaSet.has(String(fa.serviceAreaId)) || pendingServiceAreaSet.has(fa.serviceAreaName)
    );
  }, [foremanAreasList, pendingServiceAreaSet]);
  const projectsForSelection = useMemo(() => {
    if (pendingServiceAreaSet.size === 0) return projectsList;
    return projectsList.filter((p) => pendingServiceAreaSet.has(String(p.serviceAreaId)));
  }, [projectsList, pendingServiceAreaSet]);

  /** Applied service/foreman selections in wire form — also stable effect deps (strings, not arrays). */
  const appliedServiceAreaParam = toScheduleListParam(appliedFilters.serviceAreas);
  const appliedForemanAreaParam = toScheduleListParam(appliedFilters.foremanAreas);

  useEffect(() => {
    let cancelled = false;
    const loadEquipmentTypes = async () => {
      try {
        const rows = await getScheduleVehicles({
          serviceArea: appliedServiceAreaParam,
          foremanArea: appliedForemanAreaParam,
        });
        const types = Array.from(
          new Set(
            (Array.isArray(rows) ? rows : [])
              .map((v) => (v.vehicleType || 'Unspecified').trim() || 'Unspecified')
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b));
        if (!cancelled) setEquipmentTypeOptions(types);
      } catch (error) {
        console.error('Failed to load equipment types for schedule filter:', error);
        if (!cancelled) setEquipmentTypeOptions([]);
      }
    };
    void loadEquipmentTypes();
    return () => {
      cancelled = true;
    };
  }, [appliedServiceAreaParam, appliedForemanAreaParam]);

  const updatePending = useCallback((patch: Partial<ScheduleFilterFields>) => {
    setPendingFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  return (
    <Container
      fluid
      className="tw-py-3 tw-flex tw-flex-col schedule-page tw-h-full"
      style={{
        height: '100%',
        minHeight: 0,
        /* Chart scrolls inside .schedule-gantt-viewport; avoid page scroll hiding the horizontal bar */
        overflow: 'hidden',
      }}
    >
      {/* Toolbar — two segmented controls (what to draw / whose schedule) plus page actions.
          Segmented controls read as exclusive choices far better than loose outline buttons, and
          they stack into full-width rows on a phone instead of wrapping raggedly. */}
      <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-gap-3">
          {/* Chart / Table — omitted on phones, where only the table is usable (see below). */}
          {!isMobile && (
            <SegmentedControl
              aria-label="Schedule display"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'chart', label: 'Chart', icon: 'bi-bar-chart-steps' },
                { value: 'table', label: 'Table', icon: 'bi-table' },
              ]}
            />
          )}

          <SegmentedControl
            aria-label="Schedule subject"
            value={resourceOrTask}
            onChange={setResourceOrTask}
            options={[
              { value: 'task', label: 'Tasks', icon: 'bi-list-task' },
              { value: 'vehicle', label: 'Equipment', icon: 'bi-truck' },
              ...(canPeopleSchedule
                ? [{ value: 'people' as const, label: 'People', icon: 'bi-people' }]
                : []),
            ]}
          />
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 lg:tw-ml-auto">
          <Button
            type="button"
            variant={isSearchVisible ? 'primary' : 'outline-primary'}
            size="sm"
            active={Boolean(searchTerm)}
            onClick={toggleSearch}
            title={isSearchVisible ? 'Close search' : 'Search tasks'}
          >
            <i className={`bi ${isSearchVisible ? 'bi-x-lg' : 'bi-search'} sm:tw-mr-2`} aria-hidden />
            <span className="tw-hidden sm:tw-inline">
              Search
              {searchTerm
                ? ` (${searchTerm.length > 20 ? `${searchTerm.slice(0, 20)}…` : searchTerm})`
                : ''}
            </span>
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowPrintModal(true)}>
            <i className="bi bi-printer sm:tw-mr-2" aria-hidden />
            <span className="tw-hidden sm:tw-inline">Print Schedule</span>
          </Button>
        </div>
      </div>

      <FiltersCard
        title="Schedule filters"
        subtitle="Set service area, equipment, projects, and dates — click Apply to update the schedule"
        collapsible
        defaultCollapsed={false}
        showClearButton
        onClear={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        showApplyButton
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges}
      >
        <Row className="tw-items-end xl:tw-flex-nowrap">
          <Col xs={12} sm="auto">
            <MultiSelectDropdown
              label="Service Area"
              options={serviceAreasList}
              value={pendingFilters.serviceAreas}
              onChange={(vals) => {
                // Foreman areas, projects and activities are scoped by service area, so a change
                // here invalidates those selections (same reset the single-select did).
                setPendingFilters((prev) => ({
                  ...prev,
                  serviceAreas: vals.map((v) => String(v).trim()).filter((s) => s.length > 0),
                  foremanAreas: [],
                  projectIds: [],
                  activityIds: [],
                  activityNumbers: [],
                }));
              }}
              getValue={(sa) => String(sa.id)}
              formatOption={(sa) => sa.name}
              placeholder="All service areas"
              disabled={loadingFilters}
              style={{ minWidth: '160px' }}
              noOptionsMessage="No service areas available"
            />
          </Col>
          <Col xs={12} sm="auto">
            {(resourceOrTask === 'task' || resourceOrTask === 'people') && (
              <MultiSelectDropdown
                label="Foreman Area"
                options={foremanAreasForSelection}
                value={pendingFilters.foremanAreas}
                onChange={(vals) =>
                  updatePending({
                    foremanAreas: vals.map((v) => String(v).trim()).filter((s) => s.length > 0),
                  })
                }
                getValue={(fa) => String(fa.id)}
                formatOption={(fa) => fa.name}
                placeholder="All foreman areas"
                disabled={loadingFilters}
                style={{ minWidth: '180px' }}
                noOptionsMessage={
                  pendingFilters.serviceAreas.length > 0
                    ? 'No foreman areas for selected service areas'
                    : 'No foreman areas available'
                }
              />
            )}
          </Col>
          <Col xs={12} sm="auto">
            {resourceOrTask === 'vehicle' && (
              <MultiSelectDropdown
                label="Equipment Type"
                options={equipmentTypeOptions}
                value={pendingFilters.equipmentTypes}
                onChange={(vals) =>
                  updatePending({
                    equipmentTypes: vals.map((v) => String(v).trim()).filter((s) => s.length > 0),
                  })
                }
                getValue={(vehicleType) => vehicleType}
                formatOption={(vehicleType) => vehicleType}
                placeholder="All equipment types"
                style={{ minWidth: '180px' }}
                noOptionsMessage="No equipment types available"
              />
            )}
          </Col>
          <Col xs={12} md={6} lg={4} xl>
            <MultiSelectDropdown
              label="Project"
              options={projectsForSelection}
              value={pendingFilters.projectIds}
              onChange={(ids) =>
                updatePending({
                  projectIds: ids.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0),
                })
              }
              getValue={(p) => p.id}
              formatOption={(p) => (p.projectNumber ? `${p.projectNumber} - ${p.name}` : p.name)}
              placeholder="All projects"
              disabled={loadingFilters}
              style={{ minWidth: '220px', width: '100%' }}
              noOptionsMessage={
                pendingFilters.serviceAreas.length > 0
                  ? 'No projects for selected service areas'
                  : 'No projects available'
              }
            />
          </Col>
          <Col xs={12} md={6} lg={4} xl>
            <MultiSelectDropdown
              label="Activity"
              options={activityOptions}
              value={pendingFilters.activityIds}
              onChange={(ids) => {
                const cleanIds = ids.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0);
                const nums = cleanIds
                  .map((id) => activityOptions.find((a) => Number(a.id) === id))
                  .filter((a): a is Activity => Boolean(a))
                  .map((a) => String(a.activityNumber ?? '').trim())
                  .filter((s) => s.length > 0);
                updatePending({
                  activityIds: cleanIds,
                  activityNumbers: Array.from(new Set(nums)),
                });
              }}
              getValue={(a) => a.id}
              formatOption={(a) =>
                a.activityNumber ? `#${a.activityNumber} - ${a.activityName}` : a.activityName
              }
              placeholder="All activities"
              disabled={loadingFilters || loadingActivityOptions}
              style={{ minWidth: '220px', width: '100%' }}
              noOptionsMessage={
                pendingFilters.serviceAreas.length > 0
                  ? 'No activities for selected service areas'
                  : 'No activities available'
              }
            />
          </Col>
          <Col xs={12} sm="auto">
            <MultiSelectDropdown
              label="Status"
              options={SCHEDULE_PAGE_STATUS_FILTER_OPTIONS}
              value={pendingFilters.statuses}
              onChange={(vals) =>
                updatePending({
                  statuses: vals.map((v) => String(v).trim()).filter((s) => s.length > 0),
                })
              }
              getValue={(s) => s}
              formatOption={(s) => formatScheduleTaskStatusLabel(s)}
              placeholder="Planned, Active, Active marked complete"
              disabled={loadingFilters}
              style={{ minWidth: '140px' }}
              /** Statuses are already in lifecycle order; don't let the dropdown alphabetize them. */
              preserveOptionsOrder
            />
          </Col>
          <Col xs={12} sm="auto">
            {resourceOrTask === 'task' && (
              <MultiSelectDropdown
                label="Work Type"
                options={SCHEDULE_WORK_TYPE_OPTIONS}
                value={pendingFilters.workTypes}
                onChange={(vals) =>
                  updatePending({
                    workTypes: vals.map((v) => String(v).trim()).filter((s) => s.length > 0),
                  })
                }
                getValue={(workType) => workType}
                formatOption={(workType) => workType}
                placeholder="All work types"
                style={{ minWidth: '155px' }}
                /** Quantified is the primary work type; keep it first rather than alphabetized. */
                preserveOptionsOrder
              />
            )}
          </Col>
          <Col xs={12} md="auto" className="tw-flex tw-flex-wrap lg:tw-flex-nowrap tw-items-end tw-gap-2">
            <div>
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={pendingFilters.startDate}
                onChange={(e) => updatePending({ startDate: e.target.value })}
                style={{ minWidth: '140px' }}
              />
            </div>
            <div>
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={pendingFilters.endDate}
                onChange={(e) => updatePending({ endDate: e.target.value })}
                style={{ minWidth: '140px' }}
              />
            </div>
          </Col>
        </Row>
      </FiltersCard>

      {isSearchVisible && (
        <Row className="tw-mb-3 search-container tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search" aria-hidden />
              </InputGroup.Text>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search by task number or name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              />
              <Button variant="primary" size="sm" onClick={handleSearch} title="Search">
                <i className="bi bi-search" aria-hidden />
              </Button>
              {(searchInput || searchTerm) && (
                <Button variant="outline-primary" size="sm" onClick={clearSearch} title="Clear search">
                  <i className="bi bi-x-lg" aria-hidden />
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">Searching for: &quot;{searchTerm}&quot;</div>
            )}
          </Col>
        </Row>
      )}

      <div className="tw-flex tw-justify-end tw-items-center tw-mb-2 tw-gap-2 tw-flex-wrap">
        <span className="tw-text-sm tw-text-content-muted">
          Today is{' '}
          {new Date().toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          .
        </span>
      </div>

      {/* On a phone the chart toggle is hidden and we fall back to the table — say so once,
          quietly, so a user who set Chart on desktop isn't confused by the switch. */}
      {isMobile && viewMode === 'chart' && (
        <Alert variant="info" className="tw-mb-2 tw-py-2 tw-text-sm">
          <i className="bi bi-phone tw-mr-2" aria-hidden />
          Showing the table — the timeline chart needs a wider screen. Rotate your device or open
          WRAP on a tablet to see the Gantt view.
        </Alert>
      )}

      <div
        className={`schedule-page-main tw-grow tw-flex tw-flex-col min-h-0 tw-overflow-hidden`}
        style={{ minHeight: 0 }}
      >
        {effectiveViewMode === 'table' && resourceOrTask === 'vehicle' && (
          <ScheduleEquipmentTable
            filters={appliedWithSearch}
            foremanAreas={foremanAreasList}
            projects={projectsList}
          />
        )}
        {effectiveViewMode === 'table' && resourceOrTask === 'task' && (
          <ScheduleTaskTable filters={appliedWithSearch} />
        )}
        {effectiveViewMode === 'table' && resourceOrTask === 'people' && (
          <Alert variant="info" className="tw-mb-0">
            {isMobile ? (
              <>
                <i className="bi bi-people tw-mr-2" aria-hidden />
                The people schedule is a timeline view, which needs a wider screen. Try Tasks or
                Equipment here, or open WRAP on a tablet.
              </>
            ) : (
              <>
                People schedule is shown in <strong>Chart</strong> view (employee rows). Switch to Chart to see the timeline.
                <Button variant="primary" size="sm" className="tw-ml-2" onClick={() => setViewMode('chart')}>
                  Show chart
                </Button>
              </>
            )}
          </Alert>
        )}
        {effectiveViewMode === 'chart' && resourceOrTask === 'task' && (
          <TaskGanttChart filters={appliedWithSearch} scalePreset={chartTimelineScale} />
        )}
        {effectiveViewMode === 'chart' && resourceOrTask === 'vehicle' && (
          <ResourceGanttChart
            filters={appliedWithSearch}
            scheduleData={scheduleData}
            setScheduleData={setScheduleData}
            loading={loadingSchedule}
            setLoading={setLoadingSchedule}
            groupBy="vehicle"
            fillerActivities={fillerActivities}
            foremanAreas={foremanAreasList}
            defaultContractYear={defaultContractYear}
            scalePreset={chartTimelineScale}
          />
        )}
        {effectiveViewMode === 'chart' && resourceOrTask === 'people' && (
          <ResourceGanttChart
            filters={appliedWithSearch}
            scheduleData={scheduleData}
            setScheduleData={setScheduleData}
            loading={loadingSchedule}
            setLoading={setLoadingSchedule}
            groupBy="employee"
            fillerActivities={fillerActivities}
            foremanAreas={foremanAreasList}
            defaultContractYear={defaultContractYear}
            scalePreset={chartTimelineScale}
          />
        )}
      </div>

      {effectiveViewMode === 'chart' && (
        <div className="tw-flex tw-flex-wrap tw-items-stretch tw-justify-between tw-gap-3 tw-mt-2">
          <ScheduleChartLegend />
          <ScheduleTimelineScaleSwitcher
            value={chartTimelineScale}
            onChange={setChartTimelineScale}
            aria-label={resourceOrTask === 'task' ? 'Task schedule timeline scale' : 'Schedule timeline scale'}
          />
        </div>
      )}

      <PrintScheduleModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        appliedFilters={appliedFilters}
        serviceAreas={serviceAreasList}
        foremanAreas={foremanAreasList}
      />
    </Container>
  );
};

export default SchedulePage;
